import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  Plus,
  Trash2,
  Edit,
  Mail,
  MessageSquare,
  FileText,
  Copy,
  Eye
} from 'lucide-react';

const AVAILABLE_VARIABLES = [
  { name: '{{CustomerName}}', description: 'Customer\'s full name' },
  { name: '{{ServiceType}}', description: 'Type of service performed' },
  { name: '{{LastJobDate}}', description: 'Date of last completed job' },
  { name: '{{City}}', description: 'Customer\'s city' }
];

export default function TemplatesManagement() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    channel: 'email',
    subject: '',
    message_body: '',
    button_text: '',
    button_link: '',
    variables: []
  });

  const fetchTemplates = async () => {
    try {
      const response = await axiosInstance.get('/marketing/templates');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      channel: 'email',
      subject: '',
      message_body: '',
      button_text: '',
      button_link: '',
      variables: []
    });
    setEditingTemplate(null);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.message_body) {
      toast.error('Please fill in name and message body');
      return;
    }

    if (formData.channel === 'email' && !formData.subject) {
      toast.error('Email templates require a subject line');
      return;
    }

    // Extract used variables from message body
    const usedVars = AVAILABLE_VARIABLES
      .filter(v => formData.message_body.includes(v.name) || formData.subject?.includes(v.name))
      .map(v => v.name);

    try {
      const data = { ...formData, variables: usedVars };
      
      if (editingTemplate) {
        await axiosInstance.patch(`/marketing/templates/${editingTemplate.id}`, data);
        toast.success('Template updated');
      } else {
        await axiosInstance.post('/marketing/templates', data);
        toast.success('Template created');
      }
      setDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await axiosInstance.delete(`/marketing/templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      channel: template.channel,
      subject: template.subject || '',
      message_body: template.message_body,
      button_text: template.button_text || '',
      button_link: template.button_link || '',
      variables: template.variables || []
    });
    setDialogOpen(true);
  };

  const insertVariable = (variable) => {
    setFormData(prev => ({
      ...prev,
      message_body: prev.message_body + variable
    }));
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'whatsapp': return <MessageSquare className="w-4 h-4" />;
      case 'both': return <><Mail className="w-4 h-4" /><MessageSquare className="w-4 h-4" /></>;
      default: return <Mail className="w-4 h-4" />;
    }
  };

  const getPreviewContent = (template) => {
    let content = template.message_body;
    let subject = template.subject || '';
    
    // Replace variables with sample data
    const sampleData = {
      '{{CustomerName}}': 'John Doe',
      '{{ServiceType}}': 'AC Servicing',
      '{{LastJobDate}}': '2024-01-15',
      '{{City}}': 'Mumbai'
    };
    
    Object.entries(sampleData).forEach(([key, value]) => {
      content = content.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
      subject = subject.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
    });
    
    return { content, subject };
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Message Templates
            </CardTitle>
            <CardDescription>Create reusable email and WhatsApp templates</CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#595959] hover:bg-[#404040]">
                <Plus className="w-4 h-4 mr-2" /> New Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create New Template'}</DialogTitle>
                <DialogDescription>Design your message template with dynamic variables</DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Template Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Follow-up Email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Channel</Label>
                    <Select value={formData.channel} onValueChange={(v) => setFormData({ ...formData, channel: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {(formData.channel === 'email' || formData.channel === 'both') && (
                  <div className="space-y-2">
                    <Label>Email Subject *</Label>
                    <Input
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="e.g., Thank you for choosing Redefyne, {{CustomerName}}!"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Message Body *</Label>
                  <Textarea
                    value={formData.message_body}
                    onChange={(e) => setFormData({ ...formData, message_body: e.target.value })}
                    placeholder="Write your message here. Use variables like {{CustomerName}} for personalization."
                    rows={6}
                  />
                </div>

                {/* Variables Helper */}
                <div className="space-y-2">
                  <Label className="text-sm text-gray-500">Available Variables (click to insert)</Label>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_VARIABLES.map(v => (
                      <Button
                        key={v.name}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => insertVariable(v.name)}
                        className="text-xs"
                        title={v.description}
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        {v.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Button Text (optional)</Label>
                    <Input
                      value={formData.button_text}
                      onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                      placeholder="e.g., Book Now"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Button Link (optional)</Label>
                    <Input
                      value={formData.button_link}
                      onChange={(e) => setFormData({ ...formData, button_link: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSubmit} className="bg-[#595959] hover:bg-[#404040]">
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No templates yet</p>
              <p className="text-sm text-gray-400">Create your first message template</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map(template => (
                <Card key={template.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{template.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="flex items-center gap-1">
                            {getChannelIcon(template.channel)}
                            {template.channel}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setPreviewTemplate(template); setPreviewOpen(true); }}
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(template)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(template.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {template.subject && (
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Subject:</span> {template.subject}
                      </p>
                    )}

                    <p className="text-sm text-gray-500 line-clamp-3">
                      {template.message_body}
                    </p>

                    {template.variables?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {template.variables.map(v => (
                          <Badge key={v} variant="secondary" className="text-xs">
                            {v}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
            <DialogDescription>Preview with sample data</DialogDescription>
          </DialogHeader>
          {previewTemplate && (
            <div className="space-y-4">
              {previewTemplate.subject && (
                <div>
                  <Label className="text-xs text-gray-500">Subject</Label>
                  <p className="font-medium">{getPreviewContent(previewTemplate).subject}</p>
                </div>
              )}
              <div>
                <Label className="text-xs text-gray-500">Message</Label>
                <div className="bg-gray-50 rounded-lg p-4 mt-1 whitespace-pre-wrap">
                  {getPreviewContent(previewTemplate).content}
                </div>
              </div>
              {previewTemplate.button_text && (
                <Button className="w-full bg-[#595959]">
                  {previewTemplate.button_text}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
